import React, { useContext } from 'react'

import CornerControls from './common/CornerControls'
import DetailCardLayout from './common/DetailCardLayout'
import ItemTitle from './common/ItemTitle'
import NotesAndAttachments from './common/NotesAndAttachments'
import { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import TextLoadingBox from 'components/Luxkit/Typography/TextLoadingBox'
import { fareCabinLabelMap, fareTypeLabelMap } from 'constants/flight'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useAirport } from 'tripPlanner/hooks/api'
import { FlightItem } from 'tripPlanner/types/tripItem'
import { formatShortDateRange } from 'tripPlanner/utils'
import LineNotesIcon from 'components/Luxkit/Icons/line/LineNotesIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import FlightSearchPriceAndCTAs from './FlightSearchPriceAndCTAs'
import styled from 'styled-components'

const DetailsBlock = styled(BodyTextBlock)`
  clear: both;
`

function getAirportName(
  airport: App.AirportLocation | undefined,
  placeName: string,
) {
  let airportName: string | undefined

  if (airport) {
    airportName =
      airport.airportName || airport.cityAirportName || airport.cityDesc
  }
  // Fallback if no airport or if it's missing data
  if (!airportName) {
    airportName = placeName
  }
  return airportName
}

interface Props {
  item: FlightItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailFlightSearchCard({ item, variant, tripNameLabel }: Props) {
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false

  const {
    originAirportCode,
    destinationAirportCode,
    savedItemData: {
      fareClass,
      fareType,
      travellerCounts,
    } = {},
  } = item

  const { data: departureAirport, isLoading: isDepartureAirportLoading } =
    useAirport({
      code: originAirportCode,
    })

  const { data: arrivalAirport, isLoading: isArrivalAirportLoading } =
    useAirport({
      code: destinationAirportCode,
    })

  const departureAirportName = getAirportName(
    departureAirport,
    item.startPlace.name,
  )
  const arrivalAirportName = getAirportName(arrivalAirport, item.endPlace.name)
  const hasAirportNames = !!(departureAirportName && arrivalAirportName)
  const isLoadingAirportData =
    isDepartureAirportLoading || isArrivalAirportLoading

  const title = hasAirportNames ? (
    <>
      {departureAirportName} <LineArrowRightIcon size="S" />{' '}
      {arrivalAirportName}
    </>
  ) : isLoadingAirportData ? (
    <TextLoadingBox width={160} />
  ) : (
    'Flight'
  )

  const fareClassLabel = fareClass ? fareCabinLabelMap[fareClass] : undefined
  const fareTypeLabel = fareType ? fareTypeLabelMap[fareType] : undefined

  const travellerCount: App.Occupants = travellerCounts ?? {
    adults: 0,
  }

  const details = [
    formatShortDateRange(item),
    fareClassLabel,
    fareTypeLabel,
    formatOccupantsShort(travellerCount),
  ]
    .filter(Boolean)
    .join(' • ')

  return (
    <DetailCardLayout>
      <DetailCardLayout.Image></DetailCardLayout.Image>
      <DetailCardLayout.Content>
        {canEdit && <CornerControls item={item} variant={variant} />}
        <VerticalSpacer gap={8}>
          <ItemTitle icon={LinePlaneDepartureIcon}>{title}</ItemTitle>
          {details && (
            <DetailsBlock variant="medium" startIcon={<LineNotesIcon />}>
              {details}
            </DetailsBlock>
          )}
          {tripNameLabel}
          <NotesAndAttachments
            item={item}
            canEdit={canEdit}
            variant={variant}
          />
          <FlightSearchPriceAndCTAs
            item={item}
            buttonKind={variant === 'drawer' ? 'primary' : 'secondary'}
          />
        </VerticalSpacer>
      </DetailCardLayout.Content>
    </DetailCardLayout>
  )
}

export default DetailFlightSearchCard
